import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AgentAvatar from 'components/@home/@settings/Tabs/Agents/AgentAvatar';
import {
  AGENT_COLOR_KEYS,
  AGENT_COLOR_PALETTE,
  colorFromString,
} from 'components/@home/@settings/Tabs/Agents/agentColorPalette';
import { func, object, string } from 'prop-types';
import React, { useMemo } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  previewWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3, 0, 2),
  },
  label: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  swatches: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  swatch: {
    width: 40,
    height: 40,
    borderRadius: '8px',
    border: '2px solid transparent',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.divider,
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      borderColor: theme.palette.text.primary,
    },
  },
  swatchSelected: {
    borderColor: theme.palette.text.primary,
  },
  swatchDot: {
    width: 22,
    height: 22,
    borderRadius: '50%',
  },
});

const AgentColorPicker = ({ flow = null, color = '', onChange, nameSeed = '' }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const previewFlow = useMemo(
    () => ({
      ...flow,
      color: color || colorFromString(nameSeed || flow?._id || flow?.name || ''),
    }),
    [flow, color, nameSeed],
  );

  return (
    <Box>
      <Box sx={styles.previewWrap}>
        <AgentAvatar flow={previewFlow} size={96} />
      </Box>
      <Typography sx={styles.label}>{I18n.t('AgenticChatFlow.PickColor')}</Typography>
      <Box sx={styles.swatches} role="listbox" aria-label={I18n.t('AgenticChatFlow.PickColor')}>
        {AGENT_COLOR_KEYS.map(key => {
          const selected = (color || previewFlow.color) === key;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={key}
              sx={[styles.swatch, selected && styles.swatchSelected]}
              onClick={() => onChange(key)}
            >
              <Box
                component="span"
                sx={[
                  styles.swatchDot,
                  {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? AGENT_COLOR_PALETTE[key].core
                        : AGENT_COLOR_PALETTE[key].bg,
                  },
                ]}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

AgentColorPicker.propTypes = {
  flow: object,
  color: string,
  onChange: func.isRequired,
  nameSeed: string,
};

export default AgentColorPicker;
